import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { ApartmentData } from 'src/lib/fliko/models/units/apartment.data';
import { RentalUnit } from 'src/lib/fliko/models/units/unit.data';
import { SelectInputData } from 'src/app/shared/interfaces/select-input-data.interface';
import { SelectInputHelper } from 'src/app/shared/helpers/select-input.helper';
import { LocalizationService } from 'src/app/shared/services/localization.service';

export type LocalizationFilterKey = 'building' | 'floor' | 'staircase';

export class LocalizationFilters {
  private transCap = (x: string) => StrHlp.capitalize(this.ls.translate(x));

  public readonly ALL = () => SelectInputHelper.allIndicator('select.allF', this.ls);
  public readonly EMPTY = () => ({ value: '', title: StrHlp.capitalize(this.transCap('label.empty')) });
  public readonly DEFAULT_STATE = { building: 'all', floor: 'all', staircase: 'all' };

  buildings: SelectInputData<string>[] = [];
  floors: SelectInputData<string>[] = [];
  staircases: SelectInputData<string>[] = [];

  public state: { building: string; floor: string; staircase: string };

  constructor(
    units: RentalUnit[],
    private ls: LocalizationService
  ) {
    const buildings = new Set<string>();
    const floors = new Set<string>();
    const staircases = new Set<string>();

    units.forEach((unit) => {
      buildings.add(unit.blockNumber?.toString().toLocaleLowerCase() ?? '');
      floors.add(unit.floor?.toString().toLocaleLowerCase() ?? ''); // may be numbers
      staircases.add((unit as ApartmentData).staircaseNumber?.toString().toLocaleLowerCase() ?? '');
    });

    this.buildings = this.createLabels(buildings);
    this.floors = this.createLabels(floors);
    this.staircases = this.createLabels(staircases);
    this.state = { ...this.DEFAULT_STATE };
  }

  /**
   * Apply location filters to collection
   * @param data data to filter
   * @param key  name of rental unit field in data model
   */
  public filterData<T>(data: T[], key: string): T[] {
    return data.filter((x: any) => {
      const unit = x[key];
      if (!unit) {
        return false;
      }
      if (this.state.building != 'all' && (unit.blockNumber?.toString() || '').toLocaleLowerCase() !== this.state.building) {
        return false;
      }
      if (this.state.floor != 'all' && (unit.floor?.toString() || '').toLocaleLowerCase() !== this.state.floor) {
        return false;
      }
      if (
        this.state.staircase != 'all' &&
        (unit.staircaseNumber?.toString() || '').toLocaleLowerCase() !== this.state.staircase
      ) {
        return false;
      }
      return true;
    });
  }

  public setState(key: LocalizationFilterKey, value: string): void {
    this.state[key] = value;
  }

  public resetState(): void {
    this.state = this.DEFAULT_STATE;
  }

  public get inDefaultState(): boolean {
    for (const key in this.state) {
      if ((this.state as any)[key] !== 'all') return false;
    }
    return true;
  }

  private createLabels(values: Set<string>): SelectInputData<string>[] {
    const options: SelectInputData<string>[] = [];
    const hasEmpty = values.delete('');
    values.forEach((v) => options.push({ value: v, title: v.toLocaleUpperCase() }));
    options.sort((a, b) => a.value.localeCompare(b.value));

    return [this.ALL(), ...options, ...(hasEmpty ? [this.EMPTY()] : [])];
  }
}
